// src/app/services/launch-darkly.service.ts
import { Injectable } from '@angular/core';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { environment } from '../../../environments/environment';
import { RootScopeService } from './root-scope.service';

@Injectable({
  providedIn: 'root',
})
export class LaunchDarklyService {
  constructor(private rootScopeService: RootScopeService) {}

  private ldClient: LDClient.LDClient | null = null;
  private ldSdkKey = environment.launch_darkly_sdk_key;

  /**
   * Initialize the LaunchDarkly client with a provided context. If no context is passed,
   * a default anonymous user context is used. Await this before calling getFlag / onFlagChange.
   */
  async initialize(context?: LDClient.LDContext): Promise<void> {
    const ldContext: LDClient.LDContext = context ?? {
      kind: 'user',
      key: this.rootScopeService.globals.currentUserId || 'anonymous-temp-key',

    };

    this.ldClient = LDClient.initialize(this.ldSdkKey, ldContext);
    await this.ldClient.waitForInitialization();
    console.log('LaunchDarkly initialized with context:', ldContext);
  }

  /** Re-identify the LaunchDarkly client with the real (now known) user context. */
  async identifyWithCurrentUser(): Promise<void> {
    if (!this.ldClient) {
      console.warn('LaunchDarkly identify skipped: client not initialized');
      return;
    }
    const ctx: LDClient.LDContext = {
      kind: 'user',
      key: this.rootScopeService.globals.currentUserId || 'anonymous-temp-key',

    };
    try {
      await (this.ldClient as any).identify(ctx); // cast for SDK type compatibility
      console.log('LaunchDarkly re-identified with context:', ctx);
      // Immediately refresh view-contacts-flag (example) and update global signal if available
      try {
        const vc = this.getFlag('view-contacts-flag', false);
        this.rootScopeService.setViewContactsFlag(vc);
        this.rootScopeService.setMasterClientVisible(this.getFlag('master-client-visible', false));
        this.rootScopeService.setCbemVisible(this.getFlag('CBEM-visible', false));
        this.rootScopeService.setInvoiceExtractVisible(this.getFlag('invoice-extract-visible', false));
            this.rootScopeService.setReferralVisible(this.getFlag('gb-referrals-visible', false));
            this.rootScopeService.setAddGbReferralsVisible(this.getFlag('add-gb-referrals', false));
            this.rootScopeService.setEditGbReferralsVisible(this.getFlag('edit-gb-referrals', false));
            this.rootScopeService.setDeleteGbReferralsVisible(this.getFlag('delete-gb-referrals', false));
            this.rootScopeService.setDynamoUtilityVisible(this.getFlag('utility-visible', false));
      } catch (err) {
        console.warn('Could not refresh flags after identify', err);
      }
    } catch (e) {
      console.error('LaunchDarkly identify failed', e);
    }
  }

  getFlag<T extends LDClient.LDFlagValue>(flagKey: string, defaultValue: T): T {
    if (!this.ldClient) {
      return defaultValue;
    }
    return this.ldClient.variation(flagKey, defaultValue as LDClient.LDFlagValue) as T;
  }

  onFlagChange<T extends LDClient.LDFlagValue>(
    flagKey: string,
    callback: (current: T, previous: T) => void
  ): () => void {
    if (!this.ldClient) {
      // No client yet; return a noop unsubscriber
      return () => {};
    }
    const handler = (current: LDClient.LDFlagValue, previous: LDClient.LDFlagValue) => {
      callback(current as T, previous as T);
    };
    this.ldClient.on(`change:${flagKey}`, handler);
    // Return unsubscribe
    return () => {
      this.ldClient?.off(`change:${flagKey}`, handler);
    };
  }

}
